<template>
  <!-- 关闭入口：底栏「取消」与右上角 ✕，均走 handleBeforeClose（有改动先确认）。
       点遮罩、按 ESC 不响应，避免误触丢失编辑。

       宽度 576px 由列宽反算：6 列 448 + 5 个间距 50 + 行内边距 28 + 列表边框 2 + body 左右 48。
       ⚠️ 六列必须全固定宽 —— 出现任何 1fr 都会在两列之间留下空档；改列宽须同步改此宽度。 -->
  <el-dialog
    :visible.sync="innerVisible"
    width="576px"
    append-to-body
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :before-close="handleBeforeClose"
    custom-class="tag-manage-dialog"
  >
    <!-- 用 title 插槽承载「主标题 + 元信息」两行，与头部内边距、分隔线共用一套；
         勾选数单独包 <em> 以便只给数字上色 -->
    <span slot="title" class="tag-manage-title">
      <span class="tag-manage-title-text">{{ title }}</span>
      <span class="tag-manage-title-meta">
        <span>{{ subtitleMeta }}</span>
        <span v-if="tagType === 1" class="tag-manage-title-sel">已勾选 <em>{{ effectiveSelectedCount }}</em> / 6</span>
        <span v-if="!storeId" class="tag-manage-hint">保存门店后生效</span>
      </span>
    </span>

    <div class="tag-manage">
      <!-- 整块列表都是合法放置区：未 preventDefault 的区域浏览器会拒绝 drop -->
      <div ref="listWrap" class="tag-manage-list" v-loading="refreshing" element-loading-text="加载标签中"
           @dragover="onListDragOver($event)">
        <!-- 表头吸顶；放在滚动区内是为了与行共用同一宽度，滚动条占位时也能对齐 -->
        <div v-if="draft.length > 0" class="tag-manage-head">
          <span class="th-index">序号</span>
          <span></span>
          <span>标签名</span>
          <span class="th-count">字数</span>
          <span class="th-flag">状态</span>
          <span class="th-del">操作</span>
        </div>

        <div v-if="!locked && draft.length === 0" class="tag-manage-empty">
          还没有自定义标签，点击下方添加
        </div>

        <div
          v-for="(row, index) in draft"
          :key="row.uid"
          class="tag-manage-slot"
          @mousemove="onRowMouseMove($event, index)"
          @mouseleave="hoverGap = -1"
        >
          <!-- 悬停插入线：绝对定位，不占流内高度，行距恒定 -->
          <div
            v-if="hoverGap === index && !dragging && !locked"
            class="tag-gap"
            @click.stop="insertAt(index)"
          >
            <span class="tag-gap-dot">+</span>
          </div>

          <div
            class="tag-manage-row"
            :data-uid="row.uid"
            :class="{
              'is-dragging': dragging && dragFrom === index,
              'is-error': isErrorRow(row),
              'is-drop-target': dragging && dropAt === index && dropAt !== dragFrom
            }"
            @mouseenter="hoverIndex = index"
            @mouseleave="hoverIndex = -1"
            @dragover="onRowDragOver($event, index)"
            @drop="onDrop($event)"
          >
            <!-- 序号列兼作拖拽热区，悬停时换成上下移按钮（拖不准时的兜底）；
                 热区须撑满行高，只给一个字符宽鼠标抓不住 -->
            <div
              class="col-index"
              draggable="true"
              title="拖动排序"
              @dragstart="onDragStart($event, index)"
              @dragend="onDragEnd"
            >
              <template v-if="hoverIndex === index && !locked">
                <span class="tag-move" :class="{ 'is-disabled': index === 0 }" @click="moveUp(index)">↑</span>
                <span class="tag-move" :class="{ 'is-disabled': index === draft.length - 1 }" @click="moveDown(index)">↓</span>
              </template>
              <span v-else>{{ index + 1 }}</span>
            </div>

            <!-- 仅手柄列与序号列可拖（整行 draggable 会把选字识别成拖动）。
                 ⚠️ draggable 写死为 "true"，只读期由 onDragStart 里 preventDefault 拦，
                 绑成 :draggable="!locked" 会让拖拽整体失效且无提示 -->
            <span
              class="col-handle"
              draggable="true"
              title="拖动排序"
              @dragstart="onDragStart($event, index)"
              @dragend="onDragEnd"
            >⠿</span>

            <!-- 输入框用 Element 默认尺寸（40 高 / 14px 字），行高 52 = 40 + 上下各 6 -->
            <div class="col-input">
              <el-input
                :ref="'input_' + row.uid"
                v-model="row.name"
                :placeholder="'输入标签名'"
                :disabled="locked"
                @input="onNameInput(row, $event)"
                @compositionstart="isComposing = true"
                @compositionend="onCompositionEnd(row)"
                @keyup.enter.native.prevent="insertAfter(index)"
                @blur="onNameBlur(row)"
              ></el-input>
            </div>

            <span class="col-count" :class="{ 'is-limit': nameLength(row) >= 7 }">{{ nameLength(row) }}/7</span>

            <!-- 勾选状态列：只读展示，数据来自 selectedUids（勾选只在页面上改）。
                 两态都占位 —— 只写「已勾选」会让未勾选的行空着，像没有这一列 -->
            <span class="col-flag">
              <span v-if="isTagSelected(row)" class="tag-selected-flag">已勾选</span>
              <span v-else class="tag-unselected-flag">未勾选</span>
            </span>

            <div class="col-del">
              <span class="tag-del" @click="removeRow(index)">删除</span>
            </div>
          </div>

          <!-- 末尾插入线：绝对定位挂在最后一行下方，不占流内高度。
                 ⚠️ 不能改成列表末尾的流内节点 —— 会撑高内容导致滚动条反复出现，
                 且鼠标移到线上触发 mouseleave 使 hoverGap 复位，线会闪 -->
          <div
            v-if="index === draft.length - 1 && hoverGap === draft.length && !dragging && !locked"
            class="tag-gap tag-gap-end"
            @click.stop="insertAt(draft.length)"
          >
            <span class="tag-gap-dot">+</span>
          </div>
        </div>
      </div>

      <!-- 「+ 添加一条」放在滚动区外：列表超 10 行就要滚，留在里面会被滚走点不到 -->
      <div class="tag-add-bar">
        <el-button type="text" icon="el-icon-plus" :disabled="locked" @click="addRow">
          添加一条
        </el-button>
      </div>
    </div>

    <span slot="footer" class="tag-manage-footer">
      <!-- 底栏三键：重置 | 取消 | 主操作，均 Element 默认尺寸、统一 72px，用颜色深浅分主次。
           「重置」与主操作受 isDirty 控（没改过就置灰）；「取消」不置灰 —— 逃生通道，
           与右上角 ✕ 一致，任何状态下都能退出。
           ⚠️ 主操作文案走 okText，两档不可统一：有 storeId（编辑门店）叫「保存」，点了即落库；
           无 storeId（添加门店）叫「确定」，只回传草稿，建店时才入库 -->
      <el-button :disabled="locked || !isDirty" @click="handleReset">重置</el-button>
      <el-button @click="handleCancel">取消</el-button>
      <el-button
        type="primary"
        :loading="saving"
        :disabled="locked || !isDirty"
        @click="handleSave"
      >{{ okText }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
/**
 * 门店自定义标签管理弹窗（服务 / 设施共用，靠 tagType 区分）
 *
 * snapshot = 打开弹窗那一刻的池子（「重置」回到这里）；draft = 正在编辑的草稿。
 *
 * 落库时机按 storeId 分两档，主操作文案随之不同（见 okText）：
 *   有 storeId（编辑门店）——「保存」，点即 POST /storetag/save 覆盖该门店该类型的池子；
 *   无 storeId（添加门店）——「确定」，只把草稿 $emit('confirm') 回给页面，
 *                            随门店保存一起入库（storeadd 带 serviceTagList，与建店同一事务）。
 * 两档必须在界面上可辨，否则用户会误以为添加门店页的标签已经入库。
 *
 * 勾选态是只读入参（selectedUids），弹窗永不修改 —— 勾选只在页面上改。
 * 删除不可撤销：点「取消」或右上角 ✕ 关掉重开即可恢复（draft 是打开时才克隆的）。
 */
export default {
  name: 'TagManageDialog',
  props: {
    // 用 .sync 双向绑定，与项目其它弹窗一致
    visible: { type: Boolean, default: false },
    // 1 门店服务 / 2 设施设备
    tagType: { type: Number, default: 1 },
    // null = Add 页（父组件不请求列表，保存也不调接口，只把草稿回传）
    storeId: { type: Number, default: null },
    // 当前池（父组件的 serviceTags / facilityTags）
    tags: { type: Array, default: () => [] },
    // 当前页面勾选（uid 数组），只读
    selectedUids: { type: Array, default: () => [] },
    // 父组件正在拉最新池：为 true 时列表区显示 loading。
    // 注意「禁用编辑」不看它，统一看 computed.locked（refreshing 或 saving）
    refreshing: { type: Boolean, default: false },
    // 打开时要定位的冲突名（Add 页保存失败后带进来），空数组 = 普通打开
    highlightNames: { type: Array, default: () => [] }
  },
  data() {
    return {
      draft: [],
      snapshot: [],
      // 弹窗自己的发号器，打开时从 draft 现有最大值续号（否则会与页面的 tmp_1 撞号）
      tagSeed: 0,
      // 中文输入法组合输入期间不改写 v-model，否则会打断拼音
      isComposing: false,
      dragging: false,
      dragFrom: -1,
      dropAt: -1,
      hoverGap: -1,
      hoverIndex: -1,
      // 冲突行（uid 数组）。用数组不用 Set：Vue 2 不追踪 Set 的增删
      errorUids: [],
      saving: false,
      // 草稿是否已建（避免 visible / refreshing 两次 watch 重复重建、冲掉用户编辑）
      built: false
    }
  },
  computed: {
    // 重名行（uid 数组）。由 draft 派生，不手工维护 —— 改名/移动/删除后自动是最新的。
    // ⚠️ 不要改回手动维护的数组，会漏刷新，红框留在错的行上
    dupUids() {
      const seen = {}
      const dup = {}
      this.draft.forEach(r => {
        const n = this.normalizeName(r.name)
        if (!n) return
        if (seen[n]) {
          dup[seen[n]] = true
          dup[r.uid] = true
        } else {
          seen[n] = r.uid
        }
      })
      return Object.keys(dup)
    },
    // 列表只读：拉池期间与保存请求在途期间都不许改草稿 ——
    // 保存返回时用 res.list 覆盖池子，此时改动会静默丢失
    locked() {
      return this.refreshing || this.saving
    },
    innerVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    title() {
      return this.tagType === 1 ? '门店服务 · 自定义标签' : '设施设备 · 自定义标签'
    },
    // 草稿里还活着的 uid（副标题、勾选数都按草稿实时算，不能读页面数组长度）
    draftUids() {
      const set = {}
      this.draft.forEach(r => {
        set[r.uid] = true
      })
      return set
    },
    // 生效勾选数 = 页面勾选 ∩ 草稿存活 uid
    effectiveSelectedCount() {
      return this.selectedUids.filter(uid => this.draftUids[uid]).length
    },
    // 头部元信息主段；勾选数不在其中 —— 它要单独上色，模板里是独立的 <em>
    subtitleMeta() {
      return (this.tagType === 1 ? '门店服务' : '设施设备') + ' · 共 ' + this.draft.length + ' 条'
    },
    // 主操作文案：有 storeId 即落库，叫「保存」；无 storeId 只回传草稿，叫「确定」
    okText() {
      return this.storeId ? '保存' : '确定'
    },
    isDirty() {
      return JSON.stringify(this.draft) !== JSON.stringify(this.snapshot)
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.built = false
        this.errorUids = []
        // refreshing 一开始就是 false（Add 页）时也要建一次
        this.$nextTick(() => {
          if (this.visible && !this.refreshing && !this.built) this.buildDraft()
        })
      } else {
        this.built = false
        this.resetDragState()
        this.hoverGap = -1
        this.hoverIndex = -1
      }
    },
    // 包一层 $nextTick：确保同一批更新的 tags prop 已经落到 this.tags 上
    refreshing(val) {
      if (!val && this.visible && !this.built) {
        this.$nextTick(() => {
          if (this.visible && !this.built) this.buildDraft()
        })
      }
    }
  },
  methods: {
    buildDraft() {
      this.draft = this.deepClone(this.tags || [])
      this.snapshot = this.deepClone(this.tags || [])
      this.initUidSeed()
      this.built = true
      this.applyHighlight()
    },

    // 发号器从草稿里现有 tmp_N 的最大值续上，避免新发的 tmp_1 与页面已有行撞号
    initUidSeed() {
      let max = 0
      this.draft.forEach(r => {
        const m = /^tmp_(\d+)$/.exec((r && r.uid) || '')
        if (m) max = Math.max(max, Number(m[1]))
      })
      this.tagSeed = max
    },

    // 按 highlightNames 标红并滚动聚焦第一行（Add 页保存门店失败后进来）
    applyHighlight() {
      if (!this.highlightNames || this.highlightNames.length === 0) return
      const wanted = this.highlightNames.map(n => this.normalizeName(n))
      const hit = this.draft.filter(r => wanted.indexOf(this.normalizeName(r.name)) >= 0)
      this.errorUids = hit.map(r => r.uid)
      if (this.errorUids.length > 0) {
        this.$nextTick(() => {
          const wrap = this.$refs.listWrap
          if (!wrap) return
          const el = wrap.querySelector('.tag-manage-row.is-error')
          if (!el) return
          el.scrollIntoView({ block: 'center' })
          const input = el.querySelector('input')
          if (input) input.focus()
        })
      }
    },

    deepClone(obj) {
      return JSON.parse(JSON.stringify(obj == null ? null : obj))
    },

    normalizeName(s) {
      return String(s == null ? '' : s).trim().toLowerCase()
    },

    // 按码点计长度（emoji 算 1 个），与后端 codePointCount 口径一致
    nameLength(row) {
      return Array.from((row && row.name) || '').length
    },

    onNameInput(row, value) {
      // 组合输入期间截断会把拼音打断，等 compositionend 再补
      if (this.isComposing) return
      const chars = Array.from(value == null ? '' : value)
      if (chars.length > 7) row.name = chars.slice(0, 7).join('')
      // 重名看 computed.dupUids（自动跟着 draft 变）；这里只清掉该行的一次性定位红（Add 页带名进来那种）
      if (this.normalizeName(row.name)) this.clearError(row)
    },

    onCompositionEnd(row) {
      this.isComposing = false
      this.onNameInput(row, row.name)
    },

    // 失焦只做一件事：清掉该行残留的标红（例如 Add 页带名定位过来的红）。
    // 空名不标红 —— 空行只是还没填、不是错误；保存时才提示并定位到第一条空行
    onNameBlur(row) {
      this.clearError(row)
    },

    // 与草稿里其它行同名（保存前校验用；输入时的红框看 dupUids）
    isDuplicate(row) {
      const n = this.normalizeName(row.name)
      if (!n) return false
      return this.draft.some(r => r.uid !== row.uid && this.normalizeName(r.name) === n)
    },

    isErrorRow(row) {
      return this.errorUids.indexOf(row.uid) >= 0 || this.dupUids.indexOf(row.uid) >= 0
    },

    // 把这一行从标红集合里摘掉。errorUids 现在只装「Add 页带名定位」这类一次性标记：
    // 空名不标红、重名由 computed.dupUids 派生，都不经过这里
    clearError(row) {
      const i = this.errorUids.indexOf(row.uid)
      if (i >= 0) this.errorUids.splice(i, 1)
    },

    isTagSelected(row) {
      return this.selectedUids.indexOf(row.uid) >= 0
    },

    // 聚焦某一行的输入框。行已被删掉时 ref 不存在，静默跳过
    focusRow(uid) {
      this.$nextTick(() => {
        const input = this.$refs['input_' + uid]
        if (input && input.focus) input.focus()
      })
    },

    // 每次调用都真的追加一行。⚠️ 不要加「已有空行就复用」的护栏 ——
    // 最后一个空行未填时再点会毫无反应，看起来像按钮失灵；空行交给保存前校验兜住
    addRow() {
      if (this.locked) return
      this.tagSeed += 1
      this.draft.push({ id: null, name: '', sort: 0, selected: 0, uid: 'tmp_' + this.tagSeed })
      this.focusRow(this.draft[this.draft.length - 1].uid)
    },

    // 回车 / 悬停 + / 行间插入：统一走这里
    insertAt(index) {
      if (this.locked) return
      this.tagSeed += 1
      this.draft.splice(index, 0, { id: null, name: '', sort: 0, selected: 0, uid: 'tmp_' + this.tagSeed })
      this.focusRow(this.draft[index].uid)
    },

    // 回车 = 在当前行下方插入空行并聚焦。
    // ⚠️ 不要因「当前行是空的」改为跳焦点到下一行 —— 用户继续输入会落进别人的行
    insertAfter(index) {
      this.insertAt(index + 1)
    },

    // 删除不可撤销（点「取消」或 ✕ 关掉重开可恢复）。
    // 已勾选的行先确认：删掉后该勾选也随之取消
    removeRow(index) {
      if (this.locked) return
      const row = this.draft[index]
      if (!row) return
      if (!this.isTagSelected(row)) {
        this.doRemoveRow(row)
        return
      }
      const name = String(row.name == null ? '' : row.name).trim()
      this.$confirm(`「${name}」正在被勾选，删除后这条勾选也会取消，确认删除？`, '提示', {
        type: 'warning',
        confirmButtonText: '删除',
        cancelButtonText: '取消'
      })
        .then(() => this.doRemoveRow(row))
        .catch(() => {})
    },

    // 按行对象删（不按 index）：确认框是异步的，回来时下标可能已经变了
    doRemoveRow(row) {
      const i = this.draft.indexOf(row)
      if (i < 0) return
      this.draft.splice(i, 1)
      this.clearError(row)
    },

    moveUp(index) {
      if (index <= 0) return
      const row = this.draft.splice(index, 1)[0]
      this.draft.splice(index - 1, 0, row)
    },

    moveDown(index) {
      if (index >= this.draft.length - 1) return
      const row = this.draft.splice(index, 1)[0]
      this.draft.splice(index + 1, 0, row)
    },

    // ---------------- 拖拽 ----------------

    onDragStart(e, index) {
      // draggable 是写死的 "true"，只读期在这里显式拦：
      // 拦成「什么都不做」的话浏览器还会显示拖动残影，最后又 drop 不下来
      if (this.locked) {
        e.preventDefault()
        return
      }
      this.dragging = true
      this.dragFrom = index
      // 默认落点 = 原地：没划过任何行就松手时，dropAt 是 -1 会被 onDrop 判成无效直接丢弃，
      // 用户看到的就是「拖了一下没反应」
      this.dropAt = index
      this.hoverGap = -1
      e.dataTransfer.effectAllowed = 'move'
      try {
        e.dataTransfer.setData('text/plain', String(index))
      } catch (err) {
        // 某些浏览器在只读 dataTransfer 上抛错，忽略即可（不依赖它取数据）
      }
    },

    onRowMouseMove(e, index) {
      if (this.dragging || this.locked) return
      const rect = e.currentTarget.getBoundingClientRect()
      const y = e.clientY - rect.top
      // 行首 6px 内 → 插到本行前；行尾 6px 内 → 插到下一行前
      if (y < 6) {
        this.hoverGap = index
      } else if (y > rect.height - 6) {
        this.hoverGap = index + 1
      } else {
        this.hoverGap = -1
      }
    },

    // 拖拽经过行：算上半区/下半区决定插入位置，并做边缘自动滚动
    onRowDragOver(e, index) {
      if (!this.dragging) return
      e.preventDefault()
      // 必须每次重新量：容器滚动后 rect 会变，dragstart 时存的值会让自动滚动提前/滞后
      const rect = e.currentTarget.getBoundingClientRect()
      const isLowerHalf = (e.clientY - rect.top) > rect.height / 2
      this.dropAt = isLowerHalf ? index + 1 : index
      this.autoScroll(e)
    },

    autoScroll(e) {
      const wrap = this.$refs.listWrap
      if (!wrap) return
      const box = wrap.getBoundingClientRect()
      if (e.clientY - box.top < 40) {
        wrap.scrollTop -= 12
      } else if (box.bottom - e.clientY < 40) {
        wrap.scrollTop += 12
      }
    },

    onListDragOver(e) {
      if (this.dragging) e.preventDefault()
    },

    onDrop(e) {
      if (this.dragging) e.preventDefault()
      const from = this.dragFrom
      let insertAt = this.dropAt
      if (from < 0 || insertAt < 0) {
        this.resetDragState()
        return
      }
      // 拖到自己身上、或拖到自己紧邻后面 → 顺序不变
      if (insertAt === from || insertAt === from + 1) {
        this.resetDragState()
        return
      }
      const moved = this.draft.splice(from, 1)[0]
      // ★ 源被移除后，它后面的位置整体前移一位；漏掉这行会"拖了但没到位"且不报错
      if (insertAt > from) insertAt -= 1
      this.draft.splice(insertAt, 0, moved)
      this.resetDragState()
    },

    onDragEnd() {
      // dragend 一定会触发（哪怕拖到弹窗外面），复位主要放这里
      this.resetDragState()
    },

    resetDragState() {
      this.dragging = false
      this.dragFrom = -1
      this.dropAt = -1
    },

    // ---------------- 底部按钮 ----------------

    handleReset() {
      if (!this.isDirty) return
      this.$confirm('重置将丢弃本次所有修改，确认继续？', '提示', {
        type: 'warning',
        confirmButtonText: '重置',
        cancelButtonText: '继续编辑'
      })
        .then(() => {
          this.draft = this.deepClone(this.snapshot)
          this.errorUids = []
        })
        .catch(() => {})
    },

    // 底栏「取消」：不保存直接退出，确认逻辑复用 handleBeforeClose
    handleCancel() {
      this.handleBeforeClose(() => {
        this.innerVisible = false
      })
    },

    // 关闭闸门：右上角 ✕（el-dialog 的 before-close）与底栏「取消」都走这里。
    // 未修改直接关，有修改先确认
    handleBeforeClose(done) {
      // 保存请求在途时不许关 —— 请求已发出，关窗也拦不住落库，会变成「假放弃」
      if (this.saving) {
        this.$message.warning('正在保存，请稍候')
        return
      }
      if (!this.isDirty) {
        done()
        return
      }
      this.$confirm('有未保存的修改，确认关闭？', '提示', {
        type: 'warning',
        confirmButtonText: '放弃修改',
        cancelButtonText: '继续编辑'
      })
        .then(() => done())
        .catch(() => {})
    },

    // 保存前定位第一条错误：空名优先，其次重名
    findFirstError() {
      const rows = this.draft
      for (let i = 0; i < rows.length; i++) {
        if (!this.normalizeName(rows[i].name)) return rows[i]
      }
      for (let i = 0; i < rows.length; i++) {
        if (this.isDuplicate(rows[i])) return rows[i]
      }
      return null
    },

    handleSave() {
      if (this.locked) return

      // 空名不标红（空行只是还没填），只统计并定位到第一条空行；重名的红由 dupUids 负责。
      // 两类问题并存时一次说明各自的处数
      this.errorUids = []
      let emptyCount = 0
      let dupCount = 0
      this.draft.forEach(row => {
        if (!this.normalizeName(row.name)) {
          emptyCount += 1
        } else if (this.isDuplicate(row)) {
          dupCount += 1
        }
      })
      if (emptyCount > 0 || dupCount > 0) {
        this.scrollToRow(this.findFirstError())
        const parts = []
        if (emptyCount > 0) parts.push('标签名不能为空（' + emptyCount + ' 处）')
        if (dupCount > 0) parts.push('标签名重复（' + dupCount + ' 处）')
        this.$message.error(parts.join('，'))
        return
      }

      // 无 storeId（添加门店）：只回传草稿，随门店保存入库，主操作此时是「确定」。
      // 必须明说「还没落库」—— 弹窗会正常关闭、页面勾选也立刻出现，不说会被当成已保存
      if (!this.storeId) {
        this.$emit('confirm', { tagType: this.tagType, draft: this.deepClone(this.draft) })
        this.$message.success('已加入草稿，保存门店后生效')
        this.innerVisible = false
        return
      }

      this.saving = true
      const list = this.draft.map((row, i) => ({
        id: row.id,
        name: String(row.name || '').trim(),
        // sort 后端会按数组顺序重编号，这里传了只作参考
        sort: (i + 1) * 10
      }))

      this.$http
        .post('storetag/save', { storeId: this.storeId, tagType: this.tagType, list: list })
        .then(({ data: res }) => {
          if (res && res.status === 0) {
            this.$emit('saved', { tagType: this.tagType, list: res.list || [] })
            this.innerVisible = false
          } else {
            // 业务错误也是 HTTP 200，只能靠 status 判定
            this.$message.error((res && res.msg) || '保存失败')
          }
        })
        .catch(() => {
          this.$message.error('保存失败，请稍后重试')
        })
        .then(() => {
          this.saving = false
        })
    },

    // 滚到指定行并聚焦它的输入框。按 data-uid 找行，不按 .is-error ——
    // 空行已经不标红了，再按 class 找会找不到（保存时定位第一条空行就靠这里）
    scrollToRow(row) {
      if (!row) return
      const wrap = this.$refs.listWrap
      if (!wrap) return
      const el = wrap.querySelector('[data-uid="' + row.uid + '"]')
      if (!el) return
      el.scrollIntoView({ block: 'center' })
      const input = el.querySelector('input')
      if (input) input.focus()
    }
  }
}
</script>

<style scoped>
/* ============ 头部 ============ */
/* 字号 18/600，与 System/Logs.vue 的详情弹窗同一档 */
.tag-manage-dialog >>> .el-dialog__header {
  padding: 22px 24px 17px;
  /* 浅分隔线：列表本身已是带边框的卡片，头部分隔线缺失会与列表糊在一起 */
  border-bottom: 1px solid #f0f2f5;
}
/* top 24：与标题行垂直居中对齐（上内边距 22 + 行高 26/2 = 35，✕ 18px 中心 33） */
.tag-manage-dialog >>> .el-dialog__headerbtn {
  top: 24px;
  right: 18px;
}
/* ✕ 默认 20px 太大，压在右上角很抢眼；淡一档，hover 才用主色 */
.tag-manage-dialog >>> .el-dialog__headerbtn .el-dialog__close {
  font-size: 18px;
  color: #c0c4cc;
}
.tag-manage-dialog >>> .el-dialog__headerbtn:hover .el-dialog__close {
  color: #409eff;
}

/* 标题区是两行（主标题 + 元信息），所以 title 插槽当块元素用 */
.tag-manage-title {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.tag-manage-title-text {
  position: relative;
  padding-left: 12px;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  color: #303133;
}
/* 标题前的竖条：整个弹窗唯一的一处强调色，先让人看清「这是哪个弹窗」 */
.tag-manage-title-text::before {
  content: '';
  position: absolute;
  left: 0;
  top: 5px;
  width: 4px;
  height: 17px;
  border-radius: 2px;
  background: #409eff;
}
.tag-manage-title-meta {
  margin-top: 7px;
  padding-left: 12px;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: #909399;
}
.tag-manage-title-sel {
  margin-left: 12px;
}
/* 已勾选数用成功色加粗：它是这个弹窗里唯一会变、且用户真正关心的数字 */
.tag-manage-title-sel em {
  font-style: normal;
  font-weight: 600;
  color: #67c23a;
}
.tag-manage-hint {
  margin-left: 12px;
  color: #e6a23c;
}

/* body 上下内边距自行指定：Element 默认 30px 会让弹窗整体偏高 */
.tag-manage-dialog >>> .el-dialog__body {
  padding: 20px 24px 15px;
}

/* ============ 底栏 ============ */
/* 分隔线 + 统一按钮宽度：宽度不一贴在右下角会显得毛糙 */
.tag-manage-dialog >>> .el-dialog__footer {
  padding: 14px 24px 22px;
  border-top: 1px solid #f0f2f5;
}
/* 按钮组贴右；底栏内无其他元素，justify-content 是把按钮推到右边的唯一依据 */
.tag-manage-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}
/* 统一最小宽度：「保存」进入 loading 时会多一个转圈图标导致宽度跳动，给下限稳住；
   间距由 Element 的 .el-button + .el-button（10px）提供 */
.tag-manage-footer .el-button {
  min-width: 72px;
}

/* 列表自带边框与圆角，作为整体容器，同时给表头与行提供共同对齐基准；
   圆角靠 overflow 裁切，表头浅底会一并被裁掉 */
.tag-manage-list {
  /* 504 = 10 行 52px + 表头 38 + 上下留白，刚好在临界附近 */
  max-height: 504px;
  /* auto 而非 scroll：内容不足时 scroll 会画一条无滑块的空轨道 */
  overflow-y: auto;
  position: relative;
  border: 1px solid #e4e7ed;
  border-radius: 7px;
  background: #fff;
  /* 给末尾插入线的 7px 越界留位，避免被 overflow 裁掉；常量值，不随 hover 变化 */
  padding-bottom: 7px;
}
/* 细滚动条：默认 15px 在 576px 弹窗里过宽 */
.tag-manage-list::-webkit-scrollbar {
  width: 7px;
}
.tag-manage-list::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 4px;
}
.tag-manage-list::-webkit-scrollbar-thumb:hover {
  background: #909399;
}
.tag-manage-list::-webkit-scrollbar-track {
  background: transparent;
}

/* 表头吸顶。⚠️ 列宽、gap、左右 padding 必须与 .tag-manage-row 完全一致，否则列对不齐；
   z-index 取 1：盖住行内容，但须低于插入线（.tag-gap 为 2），否则「插到最前」的提示会被挡住。
   六列全固定宽，不要引入 1fr —— 弹性列会在两列之间留下空档；
   标签名 200px 是按「7 字 + 输入框内边距」留的余量，余下宽度由弹窗宽度（576px）收掉 */
.tag-manage-head {
  position: sticky;
  top: 0;
  z-index: 1;
  display: grid;
  grid-template-columns: 42px 24px 200px 54px 76px 52px;
  gap: 10px;
  align-items: center;
  height: 38px;
  padding: 0 14px;
  font-size: 13px;
  /* 加字重 + 略深底色：滚动时行从下面穿过也能分清表头 */
  font-weight: 500;
  color: #909399;
  background: #f7f8fa;
  border-bottom: 1px solid #ebeef5;
}
.th-index {
  text-align: center;
}
.th-count {
  text-align: right;
}
.th-flag {
  /* 跟 .col-flag 一起靠右，理由见 .col-flag */
  text-align: right;
}
.th-del {
  text-align: right;
}

.tag-manage-empty {
  padding: 38px 0;
  text-align: center;
  /* 用 Element 的次要文字色，跟表头/字数保持同一档，别再自造一个 #999 */
  color: #909399;
  font-size: 14px;
}

.tag-manage-slot {
  position: relative;
}

/* 行高 52 = 输入框 40 + 上下各 6；⚠️ 列宽须与 .tag-manage-head 的 grid-template-columns 同步。
   行内不设圆角：行嵌在带边框的列表里，悬停底色要铺满整条才不会两头露白角 */
.tag-manage-row {
  display: grid;
  grid-template-columns: 42px 24px 200px 54px 76px 52px;
  gap: 10px;
  align-items: center;
  height: 52px;
  padding: 0 14px;
  transition: background-color 0.15s;
}
/* 悬停高亮；⚠️ 必须写在 .is-error 之前 —— 两者特异性相同，后写的赢，
   否则报错行一悬停就变灰、红框消失 */
.tag-manage-row:hover {
  background: #f5f7fa;
}
/* 悬停时手柄加深，平时压淡以免抢视线 */
.tag-manage-row:hover .col-handle {
  color: #909399;
}
.tag-manage-row.is-error {
  /* 淡红底 + 左侧 2px 红条。用 inset 阴影不用 border-left：
     border 会改变盒尺寸，行高就不一致了；阴影不占位 */
  background: #fef0f0;
  box-shadow: inset 2px 0 0 #f56c6c;
}
.tag-manage-row.is-error:hover {
  background: #fde2e2;
}
.tag-manage-row.is-dragging {
  opacity: 0.4;
  /* 用 outline 画虚线框而不是 border：border 会改变盒尺寸，
     dragstart 里同步改尺寸在 Chrome 上有概率直接把这次拖拽取消掉（拖了没反应） */
  outline: 1px dashed #c0c4cc;
  outline-offset: -1px;
}
.tag-manage-row.is-drop-target {
  /* 同上：inset 阴影代替 border-top，免得插入提示把行撑高 2px、落点跟着抖 */
  box-shadow: inset 0 2px 0 #409eff;
}

.col-index {
  font-size: 14px;
  /* 序号是辅助信息，压到最淡的一档；悬停时才换成 ↑↓ 两个蓝箭头 */
  color: #c0c4cc;
  text-align: center;
  white-space: nowrap;
  /* 与手柄一起作为拖拽热区：撑满行高，cursor 提示可拖 */
  cursor: move;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tag-move {
  cursor: pointer;
  color: #409eff;
  padding: 0 1px;
}
.tag-move.is-disabled {
  color: #dcdfe6;
  cursor: not-allowed;
}

.col-handle {
  cursor: move;
  /* 比序号再淡一档：手柄是「知道有这功能才去找」的元素，默认不该抢视线，
     行悬停时由 .tag-manage-row:hover .col-handle 提亮 */
  color: #dfe3e8;
  text-align: center;
  user-select: none;
  /* 撑满行高与列宽：只给 ⠿ 那一个字符的可点区域，鼠标抓空就等于「拖不动」 */
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.col-count {
  font-size: 13px;
  /* 字数常态压到最淡（满篇 0/7 太吵），只有到达上限才跳红提醒 */
  color: #c0c4cc;
  text-align: right;
}
.col-count.is-limit {
  color: #f56c6c;
}

.col-flag {
  font-size: 13px;
  /* 状态列 76px、胶囊 53px，靠右后两侧空隙都是 34px 左右（10px 列间距 + 23px 余量）；
     若改回居中，左边会明显窄、右边明显宽，胶囊看着像没对齐 */
  text-align: right;
}
/* 勾选态做成胶囊：纯文字两态在 6 列里很难一眼扫出哪些被勾了，
   底色 + 圆角让「已勾选」自己成块。两态都给底色，列才齐 */
.tag-selected-flag,
.tag-unselected-flag {
  display: inline-block;
  padding: 0 7px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 21px;
}
.tag-selected-flag {
  background: #f0f9eb;
  color: #67c23a;
}
.tag-unselected-flag {
  background: #f4f4f5;
  color: #909399;
}

.col-del {
  text-align: right;
}
.tag-del {
  font-size: 14px;
  /* 默认淡，悬停才变红：删除是不可逆操作，不该是行里最显眼的字 */
  color: #c0c4cc;
  cursor: pointer;
}
.tag-del:hover {
  color: #f56c6c;
}

.tag-gap {
  position: absolute;
  /* 左右各缩进 7px：列表现在带边框，插入线贴着边框画会看成"边框的一部分" */
  left: 7px;
  right: 7px;
  top: -7px;
  height: 14px;
  line-height: 14px;
  cursor: pointer;
  z-index: 2;
}
.tag-gap::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 6px;
  border-top: 1px solid #409eff;
}
.tag-gap-end {
  /* 挂在最后一行下方：绝对定位（相对 .tag-manage-slot），不占位、不改变列表内容高度 */
  top: auto;
  bottom: -7px;
}
.tag-gap-dot {
  position: relative;
  z-index: 1;
  display: inline-block;
  width: 14px;
  height: 14px;
  line-height: 13px;
  text-align: center;
  border-radius: 50%;
  background: #409eff;
  color: #fff;
  font-size: 13px;
}

/* 「+ 添加一条」用 Element 默认的 40px 高度：行高都 52 了，可点区域不该还是一条细线。
   text 按钮自带 padding-left: 0，所以仍然跟列表左边缘对齐 */
.tag-add-bar {
  margin-top: 10px;
}
</style>
